import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Alert, Box, Typography, Divider, Fade,
} from '@mui/material';
import boxService from '../services/boxService';
import offlineApi from '../services/offlineApi';
import networkStatus from '../services/networkStatus';
import { colors } from '../styles/styleConstants';
import { formStyles, modalStyles } from '../styles/componentStyles';

/**
 * Shared "Create New Box" dialog, used by AddItem and ItemEdit.
 * On success calls onCreated(newBox); the parent refreshes its list and selects it.
 */
export default function CreateBoxDialog({ open, onClose, onCreated }) {
  const [newBoxName, setNewBoxName] = useState('');
  const [newBoxDescription, setNewBoxDescription] = useState('');
  const [creatingBox, setCreatingBox] = useState(false);
  const [createBoxError, setCreateBoxError] = useState(null);

  const reset = () => {
    setNewBoxName('');
    setNewBoxDescription('');
    setCreateBoxError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleCreateBox = async () => {
    if (!newBoxName.trim()) {
      setCreateBoxError('Please enter a box name');
      return;
    }
    setCreatingBox(true);
    setCreateBoxError(null);
    try {
      const useOfflineMode = await networkStatus.shouldUseOfflineMode();
      let newBox;
      if (useOfflineMode) {
        const boxData = {
          name: newBoxName,
          description: newBoxDescription,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          items_count: 0,
          item_count: 0,
        };
        newBox = await offlineApi.addOrUpdateBox(boxData);
        await offlineApi.queueRequest({
          method: 'POST', url: '/boxes', data: boxData,
          timestamp: Date.now(), type: 'box_create',
        });
      } else {
        newBox = await boxService.createBox({
          name: newBoxName,
          description: newBoxDescription,
        });
      }
      if (newBox && newBox.id) onCreated(newBox);
      reset();
      onClose();
    } catch (error) {
      console.error('Error creating box:', error);
      setCreateBoxError('Failed to create box. Please try again.');
    } finally {
      setCreatingBox(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: modalStyles.paper }}
      TransitionComponent={Fade}
      transitionDuration={300}
    >
      <DialogTitle sx={modalStyles.title}>
        <Typography variant="h5" sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, color: colors.darkSlateBlue }}>
          Create New Box
        </Typography>
      </DialogTitle>
      <DialogContent sx={modalStyles.content}>
        {createBoxError && (
          <Alert severity="error" sx={{ mb: 3, mt: 1, borderRadius: 2 }}>
            {createBoxError}
          </Alert>
        )}
        <Box sx={formStyles.container}>
          <TextField
            autoFocus margin="normal" label="Box Name" type="text" fullWidth required
            value={newBoxName} onChange={(e) => setNewBoxName(e.target.value)} sx={{ mb: 3 }}
          />
          <TextField
            margin="normal" label="Description (Optional)" type="text" fullWidth multiline rows={3}
            value={newBoxDescription} onChange={(e) => setNewBoxDescription(e.target.value)}
          />
        </Box>
      </DialogContent>
      <Divider sx={{ borderColor: 'rgba(0,0,0,0.08)' }} />
      <DialogActions sx={modalStyles.actions}>
        <Button onClick={handleClose} sx={{ borderRadius: 2, px: 2 }}>Cancel</Button>
        <Button
          onClick={handleCreateBox}
          variant="contained"
          color="primary"
          disabled={creatingBox || !newBoxName.trim()}
          sx={{ backgroundColor: colors.actionBlue, borderRadius: 2 }}
        >
          Create Box
        </Button>
      </DialogActions>
    </Dialog>
  );
}
